import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { CurrentUserService } from 'src/app/user/services';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { AllStakeholderGrid, AllStakeholderRow } from './models/stakeholder';
import { StakeholderService } from './services/stakeholder.service';
import { GridService } from '../shared/grid/grid.service';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoGroupingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from '../shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'ep-stakeholder',
    templateUrl: './stakeholder.component.html',
    styleUrls: ['./stakeholder.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoGroupingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class StakeholderComponent implements OnInit {
    isLoading = false;
    allowUpdating = false;
    allowAdding = false;
    allowDeleting = false;
    allowShowLog = false;
    allStakeholders = new AllStakeholderGrid();

    constructor(
        public bizGridFiltervalues: BizGridFiltervalues,
        private currentUserService: CurrentUserService,
        private stakeholderService: StakeholderService,
        private toastService: ToastService,
        private gridService: GridService) {
    }

    async ngOnInit(): Promise<void> {
        this.allowUpdating = this.currentUserService.hasPermission('Stakeholder_Action_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Stakeholder_Action_Delete');
        this.allowAdding = this.currentUserService.hasPermission('Stakeholder_Action_Create');
        this.allowShowLog = this.currentUserService.hasPermission('Stakeholder_Action_ShowLog');

        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.isLoading = true;
            this.allStakeholders = new AllStakeholderGrid();
            this.allStakeholders = await this.stakeholderService.getAllStakeholders();
        }
        catch (e) {
            console.error(e);
        }
        finally {
            this.isLoading = false;
        }
    }

    onInitNewRow(e: any): void {
        e.data = {
            StakeholderId: null,
            StakeholdersName: '',
            Description: '',
            EmailAddress: '',
            EmailAddressUrgent: '',
            EmailAddressCc: '',
            EmailAddressBcc: '',
        } as AllStakeholderRow;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as AllStakeholderRow, item => this.saveStakeholder(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as AllStakeholderRow, item => this.saveStakeholder(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as AllStakeholderRow, item => this.deleteStakeholder(item));
    };

    async deleteStakeholder(stakeholder: AllStakeholderRow): Promise<AllStakeholderRow> {
        await this.stakeholderService.deleteStakeholder(stakeholder);
        this.toastService.showToast('Stakeholder deleted...', ToastType.success);
        return stakeholder;
    }

    async saveStakeholder(stakeholder: AllStakeholderRow): Promise<AllStakeholderRow> {
        const newStk = await this.stakeholderService.saveStakeholder(stakeholder);
        this.toastService.showToast('Stakeholder saved...', ToastType.success);
        return newStk;
    }

    customEmailValidationCallback(input: any) {

        if (!input || !input.value) {
            return true;
        }

        const splitInput = input.value.split(';');

        const regexp = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

        for (const email of splitInput) {
            if (!regexp.test(email.trim())) {
                return false;
            }
        }
        return true;
    }
}
